import { Inject, Injectable, InjectionToken } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { from, Observable } from 'rxjs';
import { filter, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { FormSignatureService } from '@protocols/domain';
import type { IRetrieveMethodTypeQuery, PipelineStepDraftDto } from '@protocols/use-cases';

import { AssignedPipelinesState } from '../state/assigned-pipelines.state';
import type { MethodTypePipelineStaging } from '../state/assigned-pipelines.state';
import { AssignedResourcesState } from '../state/assigned-resources.state';
import type { ResourceContainer } from '../state/assigned-resources.state';
import { LabelConfigState } from '../state/label-config.state';
import type { StagedLabelConfig } from '../state/label-config.state';
import { LabelState } from '../state/label.state';
import type { LabelData, LabelOverride } from '../state/label.state';
import {
  MethodTypeEditorState,
  type MethodTypeDraft,
  type MethodTypeDraftSeed,
} from '../state/method-type-editor.state';
import { StagedPipelinesState } from '../state/staged-pipelines.state';
import type { StagedPipelineDraft, StagedPipelineOverride } from '../state/staged-pipelines.state';
import { StagedResourcesState } from '../state/staged-resources.state';
import type { ResourceDraftData, ResourceDraftOverride } from '../state/staged-resources.state';

export const RETRIEVE_METHOD_TYPE_QUERY = new InjectionToken<IRetrieveMethodTypeQuery>('RETRIEVE_METHOD_TYPE_QUERY');

/**
 * The method type an editor session operates on: its id plus whether it is
 * already persisted. A not-yet-persisted method type carries a client-side
 * placeholder id — there is nothing in the DB to fetch for it.
 */
export interface MethodTypeEditorContext {
  methodTypeId: string;
  persisted: boolean;
}

@Injectable()
export class MethodTypeEditorEffects {
  constructor(
    private readonly state: MethodTypeEditorState,
    private readonly assignedResourcesState: AssignedResourcesState,
    @Inject(RETRIEVE_METHOD_TYPE_QUERY)
    private readonly retrieveMethodTypeQuery: IRetrieveMethodTypeQuery,
    private readonly stagedPipelinesState: StagedPipelinesState,
    private readonly assignedPipelinesState: AssignedPipelinesState,
    private readonly stagedResourcesState: StagedResourcesState,
    private readonly labelState: LabelState,
    private readonly labelConfigState: LabelConfigState
  ) {}

  /**
   * Live canonical signature of the editing session. Single source for both
   * the facade's `hasChanges$` compare and `captureBaseline`'s snapshot.
   *
   * NOTE: declared above `captureBaseline` on purpose — its initializer reads
   * this property at construction time.
   */
  public readonly currentSignature$: Observable<string> = this.state.select(
    this.state.id$,
    this.state.draft$,
    this.assignedPipelinesState.byMethodType$,
    this.stagedPipelinesState.pendingPipelines$,
    this.stagedPipelinesState.pipelineOverrides$,
    this.assignedResourcesState.containers$,
    this.stagedResourcesState.pendingResources$,
    this.stagedResourcesState.resourceOverrides$,
    this.labelState.pendingLabels$,
    this.labelState.labelOverrides$,
    this.labelConfigState.stagedConfigs$,
    (
      methodTypeId,
      draft,
      byMethodType,
      pendingPipelines,
      pipelineOverrides,
      containers,
      pendingResources,
      resourceOverrides,
      pendingLabels,
      labelOverrides,
      stagedConfigs
    ) =>
      FormSignatureService.compute(
        this.projectSignature({
          methodTypeId,
          draft,
          byMethodType,
          pendingPipelines,
          pipelineOverrides,
          containers,
          pendingResources,
          resourceOverrides,
          pendingLabels,
          labelOverrides,
          stagedConfigs,
        })
      )
  );

  /**
   * Snapshots the session signature into `baselineSignature` so the facade's
   * `hasChanges$` can detect divergence afterwards. Triggered at every
   * seed-completion point; no self-gating needed — every input is either
   * seeded synchronously in the seed path (editor state, resource tree root)
   * or starts empty and only moves on user action (staging ledgers). Error
   * paths never trigger it: baseline stays `null`, `hasChanges$` stays false,
   * cancel closes silently (correct — nothing was seeded).
   */
  private readonly captureBaseline = this.state.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.currentSignature$),
      tap(([, signature]) => this.state.setBaselineSignature(signature))
    )
  );

  /**
   * Seed the editor from a previously-staged draft instead of fetching from
   * the DB (re-opening a method type already edited this session). Captures
   * the dirty-tracking baseline so pre-existing staged edits do not read as
   * new work.
   */
  public readonly seedFromDraft = this.state.effect((draft$: Observable<MethodTypeDraftSeed>) =>
    draft$.pipe(
      tap((draft: MethodTypeDraftSeed) => {
        this.state.seedFromDraft(draft);
        this.captureBaseline();
      })
    )
  );

  public readonly loadMethodType = this.state.effect((context$: Observable<MethodTypeEditorContext>) =>
    context$.pipe(
      tap((context) => {
        if (!context.persisted) {
          // Create mode: pristine editor carrying the placeholder id (the save
          // cascade reads it for the tmp→real remap); the same id is seeded as
          // the resource tree root so the embedded resource manager has a
          // children-loaded, pending-save node to attach to (see
          // `ResourceEditorEffects.loadResource` for the precedent).
          this.state.seedForCreate({ id: context.methodTypeId });
          this.assignedResourcesState.seed({
            root: { kind: 'method-type', id: context.methodTypeId, pendingPersistence: 'save' },
            children: [],
          });
          this.captureBaseline();
        } else {
          this.state.setLoading(true);
          this.state.setLoadException(null);
        }
      }),
      filter((context) => context.persisted),
      switchMap((context) =>
        from(this.retrieveMethodTypeQuery.execute(context.methodTypeId)).pipe(
          tapResponse(
            (output) => {
              this.state.seedFromMethodType(output);
              // Edit mode: a clean root (no pending flag) whose direct
              // children the detail read already carries, so they are
              // pre-loaded; grandchildren lazy-load on expansion.
              this.assignedResourcesState.seed({
                root: { kind: 'method-type', id: context.methodTypeId },
                children: output.assignedResources,
              });
              this.captureBaseline();
            },
            (error: Error) => this.state.setLoadException(error)
          )
        )
      )
    )
  );

  // ============================================================================
  // Signature projection
  // ============================================================================

  /*
   * Canonical fingerprint of everything a method-type editing session can
   * change: the row draft, the session's assigned-pipeline deltas, the staged
   * pipeline/resource/label ledgers and the label-config staging. Compared
   * against a baseline captured at seed to gate the discard-confirmation
   * warning. Serialized by `FormSignatureService.compute` (recursive key
   * sort), so key-insertion order never flips the signature.
   *
   * Deliberate scope choices:
   *  - `draft.id` is excluded — identity, not work (and `markPersisted` swaps
   *    it mid-save).
   *  - The pipeline slice is picked from the RAW `byMethodType` map by the
   *    EDITOR's id, never from the pointer-based current-slice selectors: the
   *    embedded manager can move the `setContext` pointer after the seed.
   *  - Only the stagedAssigned KEYS are fingerprinted (`updateAssignedPipeline`
   *    value-patches always accompany a stage op that is already captured);
   *    `persistedAssigned`/`persistedLoaded` are excluded — they load async,
   *    independently of the seed, and deltas alone are gate-free.
   *  - Resource ledger rows are subtree-filtered exactly like the resource
   *    editor (`collectSubtreeIds` + `sanitizeLedgerRow`); childIds deeper than
   *    the root are excluded (lazy-load churn). Accepted edge: expanding the
   *    tree post-open can pull a previously-staged deep override row into the
   *    subtree filter → spurious DIRTY (a warning without new work) — same
   *    class as the resource editor, safe direction.
   *  - `saving`/`saveExceptions` never participate.
   */
  private projectSignature(input: {
    methodTypeId: string | null;
    draft: MethodTypeDraft;
    byMethodType: Record<string, MethodTypePipelineStaging>;
    pendingPipelines: StagedPipelineDraft[];
    pipelineOverrides: Record<string, StagedPipelineOverride>;
    containers: Record<string, ResourceContainer>;
    pendingResources: ResourceDraftData[];
    resourceOverrides: Record<string, ResourceDraftOverride>;
    pendingLabels: LabelData[];
    labelOverrides: Record<string, LabelOverride>;
    stagedConfigs: StagedLabelConfig[];
  }): unknown {
    const { methodTypeId, draft, byMethodType, containers } = input;

    const row = {
      name: draft.name,
      assetTypeId: draft.assetTypeId,
      sampleNames: draft.sampleNames,
      config: draft.config ?? null,
      acquisitionFields: draft.acquisitionFields,
    };

    const pipelineSlice = (methodTypeId && byMethodType[methodTypeId]) || undefined;
    const assignedPipelineDeltas = {
      stagedAssignedIds: Object.keys(pipelineSlice?.stagedAssigned ?? {}).sort(),
      stagedUnassignedIds: [...(pipelineSlice?.stagedUnassignedIds ?? [])].sort(),
    };

    const stagedPipelines = {
      pending: [...input.pendingPipelines]
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((pending) => this.toPlainStagedPipeline(pending)),
      overrides: Object.keys(input.pipelineOverrides)
        .sort()
        .map((id) => this.toPlainPipelineOverride(input.pipelineOverrides[id])),
    };

    const rootChildIds = methodTypeId ? [...(containers[methodTypeId]?.childIds ?? [])].sort() : [];
    const subtreeIds = this.collectSubtreeIds(methodTypeId, containers);
    const resources = {
      rootChildIds,
      pending: input.pendingResources
        .filter((rowData) => rowData.id !== methodTypeId && subtreeIds.has(rowData.id))
        .map((rowData) => this.sanitizeLedgerRow(rowData))
        .sort((a, b) => a.id.localeCompare(b.id)),
      overrides: Object.keys(input.resourceOverrides)
        .filter((id) => id !== methodTypeId && subtreeIds.has(id))
        .sort()
        .map((id) => ({
          original: this.sanitizeLedgerRow(input.resourceOverrides[id].original),
          override: this.sanitizeLedgerRow(input.resourceOverrides[id].override),
        })),
    };

    const labels = {
      pending: [...input.pendingLabels].sort((a, b) => a.uuid.localeCompare(b.uuid)),
      overrides: Object.keys(input.labelOverrides)
        .sort()
        .map((uuid) => input.labelOverrides[uuid]),
      configs: [...input.stagedConfigs].sort((a, b) => a.stagingId.localeCompare(b.stagingId)),
    };

    return { row, assignedPipelineDeltas, stagedPipelines, resources, labels };
  }

  /**
   * Staged drafts carry structured steps whose categories are
   * `PipelineStepCategory` class instances — the strict signature service
   * rejects them; flatten to plain objects (`PipelineEditorEffects.toPlainStep`
   * parity).
   */
  private toPlainStagedPipeline(pending: StagedPipelineDraft): unknown {
    return { ...pending, steps: pending.steps.map((step) => this.toPlainStep(step)) };
  }

  /** Manual drafts hold an opaque plain-JSON `params` blob and pass through as-is. */
  private toPlainStep(step: PipelineStepDraftDto): unknown {
    if (step.mode === 'manual') return { ...step };
    return {
      ...step,
      categories: step.categories.map((category) => ({
        uuid: category.uuid,
        name: { ...category.name },
        options: category.options.map((option) => ({
          uuid: option.uuid,
          name: { ...option.name },
          thrdis: option.thrdis,
        })),
      })),
    };
  }

  /**
   * `original` is the pre-edit DB summary; its `createdAt: Date` is dropped —
   * catalog metadata, constant within a session, never user work (and the
   * strict signature service rejects `Date`s).
   */
  private toPlainPipelineOverride(override: StagedPipelineOverride): unknown {
    const { original } = override;
    return {
      original: {
        id: original.id,
        name: original.name,
        env: original.env,
        usedByMethodTypes: original.usedByMethodTypes,
        numSteps: original.numSteps,
        pending: original.pending,
      },
      draft: this.toPlainStagedPipeline(override.draft),
    };
  }

  /* BFS over `childIds` from the root container; empty when the root is unknown. */
  private collectSubtreeIds(containerId: string | null, containers: Record<string, ResourceContainer>): Set<string> {
    const ids = new Set<string>();
    if (containerId === null) return ids;
    const queue: string[] = [containerId];
    while (queue.length > 0) {
      const id = queue.shift() as string;
      if (ids.has(id)) continue;
      ids.add(id);
      for (const childId of containers[id]?.childIds ?? []) queue.push(childId);
    }
    return ids;
  }

  /*
   * `nestedResourcesCount` / `pending` are stripped — tree-derived bookkeeping
   * resynced on every tree emission; including them would flag phantom dirt.
   */
  private sanitizeLedgerRow(row: ResourceDraftData) {
    return {
      id: row.id,
      name: row.name,
      type: row.type,
      description: row.description,
      version: row.version,
      value: row.value,
      metadata: row.metadata ?? {},
      displayName: this.normalizeDisplayName(row.displayName),
      file: this.fileIdentity(row.file),
    };
  }

  /* A picked File compares by identity fields — the object itself is not signable. */
  private fileIdentity(file: File | null | undefined): { name: string; size: number; lastModified: number } | null {
    return file ? { name: file.name, size: file.size, lastModified: file.lastModified } : null;
  }

  /** Drops empty-string entries so `{}` ≡ `{en: ''}`; key sorting is the service's job. */
  private normalizeDisplayName(displayName: Record<string, string> | undefined): Record<string, string> {
    return Object.fromEntries(Object.entries(displayName ?? {}).filter(([, value]) => value !== ''));
  }
}
